import { useCallback, useMemo } from 'react';

import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isObjectCreatedPayload } from '../../lib/proposals/core/types';
import { useProposalSelectionOptional } from '../../lib/proposals/hooks/ProposalSelectionContext';
import type { SelectionState } from '../../lib/proposals/types/selection';
import type { MergedAction } from '../../lib/proposals/utils/actionMerging';
import { resolveEntityName } from '../../lib/proposals/utils/entityName';
import { ActionAccordion } from './ActionAccordion';
import { MergedCreateActionCard } from './MergedCreateActionCard';

// ─── Merged accordion entry ──────────────────────────────────────────────────

interface MergedAccordionEntryProps {
  merged: MergedAction;
  showCheckbox: boolean;
  showExecutionOrder?: boolean;
  selectionState?: SelectionState;
  isProcessing: boolean;
  orgId: number;
  proposalId: number;
  onUpdate?: (updatedAction: ProposedAction) => void;
  /** Proposal source type for context banner */
  sourceTypeName?: string;
  /** Proposal source context for context banner */
  sourceContext?: ProposalDetail['source_context'];
}

/**
 * Renders a single merged create+association group in the accordion.
 * Extracted as its own component so useCallback can be used for the toggle
 * handler.
 *
 * The group checkbox's disabled state follows the PRIMARY action only, never
 * the absorbed associations. The primary is a create, which references
 * nothing and so is never invalid (see `actionEntityRefs.ts`); an absorbed
 * association CAN be invalid when the other entity it links to won't exist
 * (that entity's create is unchecked). Disabling the group for that would
 * lock the user out of toggling a perfectly valid create — exactly the
 * wrongly-disabled-action bug projection validity replaced. The invalid link
 * is surfaced on its own chip instead (`computeVisibleAssociations`), and the
 * server prunes it at execution.
 */
export function MergedAccordionEntry({
  merged,
  showCheckbox,
  showExecutionOrder,
  selectionState,
  isProcessing,
  orgId,
  proposalId,
  onUpdate,
  sourceTypeName,
  sourceContext,
}: MergedAccordionEntryProps) {
  const { primaryAction, allActionIds } = merged;

  const allSelected = useMemo(
    () =>
      selectionState !== undefined &&
      allActionIds.every(id => selectionState.isSelected(id)),
    [selectionState, allActionIds]
  );

  const someSelected = useMemo(
    () =>
      selectionState !== undefined &&
      allActionIds.some(id => selectionState.isSelected(id)),
    [selectionState, allActionIds]
  );

  const titleOverride = useMemo(() => {
    const { payload } = primaryAction;
    const entityType =
      primaryAction.enriched_data?.entity_type_name ??
      (isObjectCreatedPayload(payload)
        ? payload.entity_type_name
        : undefined) ??
      '';
    const entityLabel = entityType
      ? getTypeLabelByName(entityType).toLowerCase()
      : 'record';
    const baseTitle = `Create new ${entityLabel}`;
    // Resolve the entity's real name from the payload (e.g. a custom object's
    // ahoy_display_name) — enriched_data.object_label for a custom create is
    // just the generic type label ("New Org Bug"). Fall back to object_label —
    // except for notes: a title-less note's object_label is its body preview,
    // which the card renders right below the header, so suffixing it here would
    // just duplicate the body. The header stays a bare "Create new note".
    const name =
      resolveEntityName(
        isObjectCreatedPayload(payload) ? payload.payload.properties : {},
        entityType
      ) ??
      (entityType === 'ahoy_note'
        ? undefined
        : primaryAction.enriched_data?.object_label);
    return name ? `${baseTitle}: ${name}` : baseTitle;
  }, [primaryAction]);

  // Use the context's toggleMergedGroup when available (handles debounced API sync).
  // Fall back to individual toggleAction calls when context is not present.
  const selectionCtx = useProposalSelectionOptional();

  const handleToggle = useCallback(() => {
    if (!selectionState) {
      return;
    }
    if (selectionCtx) {
      selectionCtx.toggleMergedGroup(allActionIds);
      return;
    }
    // Fallback: toggle each constituent ID individually.
    if (allSelected) {
      for (const id of allActionIds) {
        if (selectionState.isSelected(id)) {
          selectionState.toggleAction(id);
        }
      }
    } else {
      for (const id of allActionIds) {
        if (!selectionState.isSelected(id)) {
          selectionState.toggleAction(id);
        }
      }
    }
  }, [selectionState, selectionCtx, allActionIds, allSelected]);

  // Show card as selected when at least one constituent ID is selected.
  // This prevents the entire card from greying out on partial selection.
  const isSelected = someSelected || !selectionState;

  return (
    <ActionAccordion
      action={primaryAction}
      showCheckbox={showCheckbox}
      showExecutionOrder={showExecutionOrder}
      isSelected={isSelected}
      isCheckboxDisabled={selectionState?.isDisabled(primaryAction.id) ?? false}
      disabledReason={
        selectionState?.getDisabledReason(primaryAction.id) ?? null
      }
      onSelectionChange={handleToggle}
      isProcessing={isProcessing}
      orgId={orgId}
      titleOverride={titleOverride}
    >
      <MergedCreateActionCard
        merged={merged}
        orgId={orgId}
        proposalId={proposalId}
        onUpdate={onUpdate}
        sourceTypeName={sourceTypeName}
        sourceContext={sourceContext}
      />
    </ActionAccordion>
  );
}
